import React, { useState, useEffect, useRef } from "react";
import "./ToolProject.css";
import { getIcon } from "../../../utils/icons";
import { useDispatch, useSelector } from 'react-redux'; 
import { setCommandType, resetOperation, setCommandMessage } from "../../../redux/operationSlice";
import { incrementUpdateCounter } from "../../../redux/utilsSlice";
import { COMMANDS } from "../../../utils/commonProperties";
import {
  addFloorPlan,
  propagateMimariPlansToTabs,
  FLOOR_TYPES,
  selectHasGroundFloor,
  selectHasMezzanine,
  selectHasGallery,
  selectHasRoof,
  selectBasementCount,
  selectNormalFloorCount,
  selectTotalRealFloorCount
} from "../../../redux/floorPlanSlice";
import { setActiveTab, togglePafta } from "../../../redux/projectTabsSlice";
import FloorPlanSettingsPopup from "./FloorPlanSettingsPopup";
import { addSitePlan } from "../../../redux/utilsSlice";
import { clearSelection } from "../../../redux/selectionSlice";
import * as THREE from 'three';

/**
 * Sadece görünen geometry'leri hesaba katarak bounding box hesaplar
 * Base point, invisible objeler ve userData boundary'leri hariç tutulur
 */
function calculateVisibleBoundingBox(object) {
  const box = new THREE.Box3();

  // Önce tüm world matrix'leri güncelle
  object.updateMatrixWorld(true);

  object.traverse((child) => {
    // Sadece görünen objeler
    if (!child.visible) return;

    // Geometry'si olan objeler için bounding box hesapla
    if (child.geometry) {
      const childBox = new THREE.Box3();

      if (child.geometry.boundingBox === null) {
        child.geometry.computeBoundingBox();
      }

      if (child.geometry.boundingBox) {
        childBox.copy(child.geometry.boundingBox);
        childBox.applyMatrix4(child.matrixWorld);
        box.union(childBox);
      }
    }
  });

  // Eğer hiçbir geometry bulunamadıysa, fallback olarak setFromObject kullan
  if (box.isEmpty()) {
    box.setFromObject(object);
  }

  return box;
}

/**
 * Orijinal objeden world matrix'lerini TRAVERSE SIRASINDA toplar (clone'dan ÖNCE çağrılmalı).
 * Ordered array döner — bakeWorldTransform ile aynı traverse sırası garanti edilir.
 * Pattern hatch children ATLANIR: bu geometry'ler clone'a dahil edilmeyecek
 * (binlerce vertex → deep-clone + applyMatrix4 → sayfa donması).
 * @param {THREE.Object3D} object - Orijinal obje
 * @returns {THREE.Matrix4[]} - geometry child'ların matrixWorld dizisi (traverse sırası)
 */
function collectWorldMatrices(object) {
  const matrices = [];
  object.updateMatrixWorld(true);
  object.traverse((child) => {
    // Pattern hatch LineSegments: clone'a dahil edilmeyecek → matris de toplanmaz.
    if (child.parent?.userData?.isPatternHatch) return;
    if (child.geometry) {
      matrices.push(child.matrixWorld.clone());
    }
  });
  return matrices;
}

/**
 * Clone sonrası: pattern hatch group'larının LineSegments child'larını kaldır.
 * THREE.js clone() geometry'yi PAYLAŞIR (deep-clone etmez). Bu child'ları kaldırmadan
 * combined traverse'daki geometry.clone() çağrısı 50K+ vertex'i kopyalar → freeze.
 * Kaldırılan child'lar dispose edilmez — parent (original) hâlâ kullanıyor.
 */
function removePatternHatchChildren(clonedObject) {
  const toRemove = [];
  clonedObject.traverse((child) => {
    if (child.userData?.isPatternHatch) {
      // Bu group'un tüm children'ı (LineSegments) toplanır
      for (const c of child.children) toRemove.push(c);
    }
  });
  toRemove.forEach(c => c.parent?.remove(c));
}

/**
 * Bir nesneyi world koordinatlarına "bake" eder.
 * orderedMatrices: collectWorldMatrices() sonucu — geometry child'ların traverse sırasında
 * toplanmış matrixWorld dizisi. clone() aynı yapıyı korur → sıra eşleşmesi garantilidir.
 * Tek traverse: geometry bake + group sıfırlama aynı geçişte → 3 traverse'den 1'e düşer.
 * @param {THREE.Object3D} clonedObject - Klonlanmış obje
 * @param {THREE.Matrix4[]} orderedMatrices - Orijinal objeden sıralı matrixWorld dizisi
 */
function bakeWorldTransform(clonedObject, orderedMatrices) {
  if (orderedMatrices && orderedMatrices.length > 0) {
    let idx = 0;
    clonedObject.traverse((child) => {
      if (child.geometry) {
        const worldMatrix = idx < orderedMatrices.length ? orderedMatrices[idx++] : null;
        if (worldMatrix) {
          // TROIKA mtext: geometry clone(true)'da SIFIRLANIR → applyMatrix4 NO-OP olur.
          // World pos + scale'i userData'ya yaz; recreateTroikaText bunu kullanır.
          const isTroika = typeof child.sync === 'function' && typeof child.text === 'string';
          if (isTroika) {
            const p = new THREE.Vector3(), q = new THREE.Quaternion(), s = new THREE.Vector3();
            worldMatrix.decompose(p, q, s);
            child.userData.bakedTextPos = { x: p.x, y: p.y, z: p.z };
            child.userData.bakedTextScale = (Number.isFinite(s.x) && s.x > 0) ? s.x : 1;
          }
          child.geometry.applyMatrix4(worldMatrix);
        }
        child.position.set(0, 0, 0);
        child.rotation.set(0, 0, 0);
        child.scale.set(1, 1, 1);
        child.updateMatrix();
      } else if (child !== clonedObject) {
        // Ara grup/node: transform sıfırla (iç içe bloklar dahil)
        child.position.set(0, 0, 0);
        child.rotation.set(0, 0, 0);
        child.scale.set(1, 1, 1);
        child.updateMatrix();
      }
    });
  } else {
    // Fallback: orijinal matris yoksa eski yöntem
    clonedObject.updateMatrixWorld(true);
    clonedObject.traverse((child) => {
      if (child.geometry) child.geometry.applyMatrix4(child.matrixWorld);
      child.position.set(0, 0, 0);
      child.rotation.set(0, 0, 0);
      child.scale.set(1, 1, 1);
      child.updateMatrix();
    });
  }
  clonedObject.position.set(0, 0, 0);
  clonedObject.rotation.set(0, 0, 0);
  clonedObject.scale.set(1, 1, 1);
  clonedObject.updateMatrix();
}

/**
 * Nesneyi ve tüm children'larını belirtilen offset ile taşır
 * Nested group yapılarını da işler
 */
function offsetAllGeometries(object, offset) {
  object.traverse((child) => {
    if (child.geometry && child.geometry.attributes && child.geometry.attributes.position) {
      const positions = child.geometry.attributes.position;
      for (let i = 0; i < positions.count; i++) {
        positions.setX(i, positions.getX(i) - offset.x);
        positions.setY(i, positions.getY(i) - offset.y);
        positions.setZ(i, positions.getZ(i) - offset.z);
      }
      positions.needsUpdate = true;
      child.geometry.computeBoundingBox();
      child.geometry.computeBoundingSphere();
    }
    // TROIKA mtext: geometry'si boş (clone'da sıfırlanır), konumu userData.bakedTextPos'ta
    // tutulur (bkz. bakeWorldTransform). Geometry'ler center'a normalize edilirken
    // bakedTextPos da AYNI offset'le kaydırılmalı; yoksa text'ler çizgilerden sabit
    // center kadar ötelenir (yön seçime/bbox center'a göre değişir).
    const btp = child.userData?.bakedTextPos;
    if (btp && typeof btp.x === 'number') {
      btp.x -= offset.x;
      btp.y -= offset.y;
      btp.z = (btp.z || 0) - offset.z;
    }
  });
}

const ToolProject = ({ scene, scenesRef }) => {
  const [activeButton, setActiveButton] = useState(null);
  const [isOpen, setIsOpen] = useState(true);
  const [floorMenuOpen, setFloorMenuOpen] = useState(false);
  const [floorDropdownPos, setFloorDropdownPos] = useState({ top: 0, left: 0, width: 0 });
  const floorMenuTriggerRef = useRef(null);
  const [selectedFloorType, setSelectedFloorType] = useState(null); // Seçilen kat türü
  const [isFloorSettingsOpen, setIsFloorSettingsOpen] = useState(false);

  // [SEÇİM MODU] Kat/Vaziyet planı oluşturulurken seçilen orijinal nesneler
  // sahneden SİLİNMİYOR, sadece visible=false ile gizleniyor. Kullanıcı
  // "Seçimleri Kaydet" butonuna basınca tüm gizlenenler scene'e görünür hale
  // döner. Bu sayede mimari paftası seçim modu sonrasında ilk haline döner.
  const hiddenObjectsRef = useRef([]);
  const [hiddenCount, setHiddenCount] = useState(0); // re-render trigger
  const [selectionsSaved, setSelectionsSaved] = useState(false); // "Proje Oluştur" görünürlük flag

  // floorPlanSlice'tan kat planı sayısı (Proje Oluştur butonu görünürlük için)
  const floorPlansCount = useSelector(state => {
    const tabs = state?.floorPlans?.tabs || {};
    let total = 0;
    for (const t of Object.values(tabs)) total += (t?.allIds?.length || 0);
    return total;
  });
  const buttonSize = 30;
  const dispatch = useDispatch();
  const commandType = useSelector((state) => state.operation.commandType);
  const selectedObjectIds = useSelector((state) => state.selection.selectedObjectIds);
  const enabledPaftaIds = useSelector((state) => state.projectTabs.enabledPaftaIds);
  const tabs = useSelector((state) => state.projectTabs.tabs);

  // Kat durumu selector'ları
  const hasGroundFloor = useSelector(selectHasGroundFloor);
  const hasMezzanine = useSelector(selectHasMezzanine);
  const hasGallery = useSelector(selectHasGallery);
  const hasRoof = useSelector(selectHasRoof);
  const basementCount = useSelector(selectBasementCount);
  const normalFloorCount = useSelector(selectNormalFloorCount);
  const totalRealFloorCount = useSelector(selectTotalRealFloorCount);

  const DOCUMENT_OFFSET = 50;
  const headerHeight = 50;

  // ✅ Seçim modu kontrolleri
  const isFloorPlanMode = commandType === COMMANDS.SELECT_FLOOR_PLAN;
  const isSitePlanMode = commandType === COMMANDS.SELECT_SITE_PLAN;
  const isDeleteMode = commandType === COMMANDS.DELETE_FROM_SCENE;
  const isInSelectionMode = isFloorPlanMode || isSitePlanMode || isDeleteMode;

  // TIKLANDIĞINDA — Proje Oluştur
  // Mimari tab'dayken tıklanırsa: mimari'deki kat planlarını tüm enabled
  // disiplin paftalarına KOPYALA (electrical/weak_current/...) ve ilk
  // disiplin paftasına geçer.
  const updateAllProject = () => {
    // [SAVE-LOAD-REWRITE] Mimari kat planlarını disiplinlere DAHIL ET.
    const firstProjectTab = tabs.find(t => t.id !== 'mimari');
    const targetTabIds = new Set();
    enabledPaftaIds.forEach(id => { if (id !== 'mimari') targetTabIds.add(id); });
    if (firstProjectTab) {
      const targetTabId = firstProjectTab.id;
      targetTabIds.add(targetTabId);
      if (!enabledPaftaIds.includes(targetTabId)) {
        dispatch(togglePafta(targetTabId));
      } else {
        dispatch(setActiveTab(targetTabId));
      }
    }

    // [SAVE-LOAD-REWRITE] Hedef paftaların scene'ini TAMAMEN TEMIZLE.
    // Sebep: Mevcut projede önceden sızıntı kaldıysa veya eski mainFrame
    // varsa, yeni proje boş scene üzerine inşa edilmeli. useElectricalLayout
    // sonraki tetiklenmede fresh mainFrame oluşturacak.
    if (scenesRef?.current) {
      for (const tabId of targetTabIds) {
        const tabScene = scenesRef.current[tabId];
        if (!tabScene) continue;
        while (tabScene.children.length > 0) {
          const obj = tabScene.children[0];
          obj.traverse?.(c => {
            if (c.geometry) c.geometry.dispose?.();
            if (c.material) {
              if (Array.isArray(c.material)) c.material.forEach(m => m.dispose?.());
              else c.material.dispose?.();
            }
          });
          tabScene.remove(obj);
        }
      }
    }

    // Mimari planlarını hedef tab'lara kopyala (idempotent — duplicate yok)
    if (targetTabIds.size > 0) {
      dispatch(propagateMimariPlansToTabs({ targetTabIds: Array.from(targetTabIds) }));
    }

    dispatch(incrementUpdateCounter());
    const command = COMMANDS[`Update`];
    if (commandType === command) {
      dispatch(resetOperation());
    } else {
      dispatch(setCommandType(command));
      dispatch(setCommandMessage("İlk noktayı seçin"));
    }
  };

  // ✅ Mevcut kat durumuna göre menü seçeneklerini oluştur
  const getFloorMenuOptions = () => {
    const options = [];

    // Zemin kat yoksa sadece zemin kat eklenebilir
    if (!hasGroundFloor) {
      options.push({
        type: FLOOR_TYPES.GROUND,
        number: 0,
        label: 'Zemin Kat Ekle',
        name: 'Zemin Kat'
      });
      return options;
    }

    // Zemin kat varsa diğer seçenekler
    // Bodrum Kat (her zaman eklenebilir, sayı azalarak)
    const nextBasementNumber = -(basementCount + 1);
    options.push({
      type: FLOOR_TYPES.BASEMENT,
      number: nextBasementNumber,
      label: `${Math.abs(nextBasementNumber)}. Bodrum Kat Ekle`,
      name: `${Math.abs(nextBasementNumber)}. Bodrum Kat`
    });

    // Asma Kat (en fazla 1 tane, opsiyonel)
    if (!hasMezzanine) {
      options.push({
        type: FLOOR_TYPES.MEZZANINE,
        number: 0,
        label: 'Asma Kat Ekle',
        name: 'Asma Kat'
      });
    }

    // Galeri Kat (en fazla 1 tane, opsiyonel)
    if (!hasGallery) {
      options.push({
        type: FLOOR_TYPES.GALLERY,
        number: 0,
        label: 'Galeri Kat Ekle',
        name: 'Galeri Kat'
      });
    }

    // Normal Kat (sayı artarak - floorCount toplamını kullan)
    const nextNormalNumber = normalFloorCount + 1;
    options.push({
      type: FLOOR_TYPES.NORMAL,
      number: nextNormalNumber,
      label: `${nextNormalNumber}. Normal Kat Ekle`,
      name: `${nextNormalNumber}. Normal Kat`
    });

    // Çatı Kat (en fazla 1 tane)
    if (!hasRoof) {
      options.push({
        type: FLOOR_TYPES.ROOF,
        number: 0,
        label: 'Çatı Kat Ekle',
        name: 'Çatı Kat'
      });
    }

    return options;
  };

  // ✅ Kat Planı butonuna tıklama - menüyü aç/kapat
  const handleFloorPlanButtonClick = () => {
    if (isFloorPlanMode) {
      // Zaten seçim modundayız, seçim varsa planı oluştur
      if (selectedObjectIds && selectedObjectIds.length > 0) {
        createFloorPlan();
      }
      return;
    }
    // Menüyü aç/kapat
    if (!floorMenuOpen && floorMenuTriggerRef.current) {
      const rect = floorMenuTriggerRef.current.getBoundingClientRect();
      setFloorDropdownPos({ top: rect.bottom + 2, left: rect.left, width: rect.width });
    }
    setFloorMenuOpen(!floorMenuOpen);
  };

  // ✅ Menüden kat türü seçildiğinde
  const handleFloorTypeSelect = (option) => {
    setSelectedFloorType(option);
    setFloorMenuOpen(false);
    // Seçim moduna gir
    dispatch(clearSelection());
    dispatch(setCommandType(COMMANDS.SELECT_FLOOR_PLAN));
    dispatch(setCommandMessage(`${option.name} için nesneleri seçin, sağ tıklayarak onaylayın`));
  };

  // ✅ Kat Planı seçim modunu başlat (eski fonksiyon - geriye uyumluluk için)
  const startFloorPlanSelection = () => {
    if (isFloorPlanMode) {
      // Zaten seçim modundayız, seçim varsa planı oluştur
      if (selectedObjectIds && selectedObjectIds.length > 0) {
        createFloorPlan();
      }
      return;
    }
    // Seçim moduna gir
    dispatch(clearSelection());
    dispatch(setCommandType(COMMANDS.SELECT_FLOOR_PLAN));
    dispatch(setCommandMessage("Kat planına eklenecek nesneleri seçin, sağ tıklayarak onaylayın"));
  };

  // ✅ Vaziyet Planı seçim modunu başlat
  const startSitePlanSelection = () => {
    if (isSitePlanMode) {
      // Zaten seçim modundayız, seçim varsa planı oluştur
      if (selectedObjectIds && selectedObjectIds.length > 0) {
        createSitePlan();
      }
      return;
    }
    // Seçim moduna gir
    dispatch(clearSelection());
    dispatch(setCommandType(COMMANDS.SELECT_SITE_PLAN));
    dispatch(setCommandMessage("Vaziyet planına eklenecek nesneleri seçin, sağ tıklayarak onaylayın"));
  };

  // ✅ Sahneden silme modunu başlat
  const startDeleteSelection = () => {
    if (isDeleteMode) {
      if (selectedObjectIds && selectedObjectIds.length > 0) {
        deleteFromScene();
      }
      return;
    }
    dispatch(clearSelection());
    dispatch(setCommandType(COMMANDS.DELETE_FROM_SCENE));
    dispatch(setCommandMessage("Silinecek nesneleri seçin, Enter ile onaylayın"));
  };

  // ✅ Seçili nesneleri sahneden sil
  const deleteFromScene = () => {
    if (!scene || !selectedObjectIds || selectedObjectIds.length === 0) return;

    const currentSelectedIds = [...selectedObjectIds];
    dispatch(clearSelection());

    setTimeout(() => {
      const objectsToRemove = [];
      scene.traverse((child) => {
        if (child.userData?.id && currentSelectedIds.includes(child.userData.id)) {
          let parentAlsoSelected = false;
          let parent = child.parent;
          while (parent && parent.type !== 'Scene') {
            if (parent.userData?.id && currentSelectedIds.includes(parent.userData.id)) {
              parentAlsoSelected = true;
              break;
            }
            parent = parent.parent;
          }
          if (!parentAlsoSelected) {
            objectsToRemove.push(child);
          }
        }
      });

      objectsToRemove.forEach(obj => {
        if (obj.parent) {
          obj.parent.remove(obj);
        } else {
          scene.remove(obj);
        }
        obj.traverse((child) => {
          if (child.geometry) child.geometry.dispose();
          if (child.material) {
            if (Array.isArray(child.material)) {
              child.material.forEach(m => m.dispose && m.dispose());
            } else {
              child.material.dispose && child.material.dispose();
            }
          }
        });
      });

      dispatch(resetOperation());
    }, 100);
  };

  // ✅ Sağ tıklama veya Enter ile seçimi onayla
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (event.key === 'Enter' && isInSelectionMode) {
        if (selectedObjectIds && selectedObjectIds.length > 0) {
          if (isFloorPlanMode) {
            createFloorPlan();
          } else if (isSitePlanMode) {
            createSitePlan();
          } else if (isDeleteMode) {
            deleteFromScene();
          }
        }
      } else if (event.key === 'Escape') {
        if (isInSelectionMode) {
          // Seçim modunu iptal et
          setSelectedFloorType(null);
          dispatch(clearSelection());
          dispatch(resetOperation());
        }
        // Menü açıksa kapat
        if (floorMenuOpen) {
          setFloorMenuOpen(false);
        }
      }
    };

    // ✅ Sağ tıklama ile seçimi tamamla (Enter'a gerek kalmadan)
    const handleContextMenu = (event) => {
      if (isInSelectionMode && selectedObjectIds && selectedObjectIds.length > 0) {
        event.preventDefault(); // Varsayılan sağ tık menüsünü engelle
        if (isFloorPlanMode) {
          createFloorPlan();
        } else if (isSitePlanMode) {
          createSitePlan();
        } else if (isDeleteMode) {
          deleteFromScene();
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('contextmenu', handleContextMenu);

    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('contextmenu', handleContextMenu);
    };
  }, [isInSelectionMode, isFloorPlanMode, isSitePlanMode, isDeleteMode, selectedObjectIds, floorMenuOpen]);

  // ✅ Menü dışına tıklandığında menüyü kapat
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (floorMenuOpen && !event.target.closest('.floor-menu-container')) {
        setFloorMenuOpen(false);
      }
    };

    if (floorMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [floorMenuOpen]);

  // ✅ Seçim modunda cursor'ı değiştir
  useEffect(() => {
    if (isInSelectionMode) {
      document.body.style.cursor = 'crosshair';
    } else {
      document.body.style.cursor = '';
    }

    return () => {
      document.body.style.cursor = '';
    };
  }, [isInSelectionMode]);

  // ✅ Kat Planı oluştur
  const createFloorPlan = () => {
    if (!scene || !selectedObjectIds || selectedObjectIds.length === 0) {
      console.warn("Scene bulunamadı veya seçili nesne yok");
      return;
    }

    // selectedObjectIds'i kopyala (clearSelection sonrasında kaybolacak için)
    const currentSelectedIds = [...selectedObjectIds];
    // Set ile O(1) lookup — Array.includes O(n) ile 3700×3700 = 13M karşılaştırmadan kaçın
    const selectedIdSet = new Set(currentSelectedIds);

    // Seçimi iptal et - bu şekilde objeler orijinal renklerine döner
    dispatch(clearSelection());

    // Kısa bir süre bekleyerek nesnelerin orijinal renklerine dönmesini sağla
    setTimeout(() => {
      const selectedObjects = [];

      // Sadece üst seviye seçili nesneleri bul (nested children'ları dahil etme)
      scene.traverse((child) => {
        if (child.userData?.id && selectedIdSet.has(child.userData.id)) {
          // Bu nesnenin parent'ı da seçili mi kontrol et
          let parentAlsoSelected = false;
          let parent = child.parent;
          while (parent && parent.type !== 'Scene') {
            if (parent.userData?.id && selectedIdSet.has(parent.userData.id)) {
              parentAlsoSelected = true;
              break;
            }
            parent = parent.parent;
          }
          // Sadece parent'ı seçili olmayan nesneleri ekle
          if (!parentAlsoSelected) {
            selectedObjects.push(child);
          }
        }
      });

      if (selectedObjects.length === 0) {
        console.warn("Seçili nesneler scene'de bulunamadı");
        return;
      }

      const group = new THREE.Group();
      selectedObjects.forEach(obj => {
        // ✅ World matrix'leri CLONE'DAN ÖNCE topla (blok transform'ları için kritik)
        // collectWorldMatrices pattern hatch children'ı ATLAR (orderedMatrices ile senkron)
        const originalMatrices = collectWorldMatrices(obj);

        const clonedObj = obj.clone();

        // Pattern hatch LineSegments'lerini clone'dan kaldır (THREE.js clone geometry'yi PAYLAŞIR).
        // Bu geometry'ler clone edilmezse binlerce vertex'in kopyalanması önlenir.
        // Orijinal hatch geometry'si etkilenmez; hatch group'ları boş kalır (layout'ta görünmez).
        removePatternHatchChildren(clonedObj);

        // Geometry/material deep clone + renk geri yükleme + seçim temizliği TEK geçişte.
        // Eski kod: 3 ayrı traverse + getObjectByProperty O(n²) bug → kaldırıldı.
        // clone() userData'yı korur → originalColor doğrudan okunabilir.
        clonedObj.traverse((clonedChild) => {
          if (clonedChild.geometry) clonedChild.geometry = clonedChild.geometry.clone();
          if (clonedChild.material) {
            clonedChild.material = Array.isArray(clonedChild.material)
              ? clonedChild.material.map(m => m.clone())
              : clonedChild.material.clone();
            const origColor = clonedChild.userData?.originalColor;
            if (origColor !== undefined) {
              if (Array.isArray(clonedChild.material)) {
                clonedChild.material.forEach((mat, i) => {
                  if (mat.color && Array.isArray(origColor) && origColor[i] !== undefined) mat.color.setHex(origColor[i]);
                });
              } else if (clonedChild.material.color && typeof origColor === 'number') {
                clonedChild.material.color.setHex(origColor);
              }
            }
          }
          if (clonedChild.userData) {
            delete clonedChild.userData.isSelected;
            delete clonedChild.userData.selectedTime;
          }
        });

        // ✅ World transform'u geometry'ye bake et (orijinal matrisler kullanarak)
        bakeWorldTransform(clonedObj, originalMatrices);

        group.add(clonedObj);
      });

      // Matrix'leri güncelle (bounding box doğru hesaplansın)
      group.updateMatrixWorld(true);

      // Sadece görünen geometry'leri hesaba katarak bounding box hesapla
      const boundingBox = calculateVisibleBoundingBox(group);
      const size = boundingBox.getSize(new THREE.Vector3());
      const center = boundingBox.getCenter(new THREE.Vector3());

      // Objeleri merkeze normalize et (geometry vertex'lerini kaydır)
      offsetAllGeometries(group, center);

      // Seçilen kat türü bilgilerini al (varsayılan: Normal Kat)
      const floorType = selectedFloorType?.type || FLOOR_TYPES.NORMAL;
      const floorNumber = selectedFloorType?.number ?? 1;
      const floorName = selectedFloorType?.name || "Kat Planı";

      const floorPlanData = {
        name: floorName,
        type: floorType,
        number: floorNumber,
        width: Math.ceil(size.x) + 12 * DOCUMENT_OFFSET, // Yatay iç ofset 6 katı (3x artırıldı)
        height: Math.ceil(size.y) + 6 * DOCUMENT_OFFSET + headerHeight, // Dikey iç ofset 3 katı (3x artırıldı)
        objects: selectedObjects,
        objectsIds: currentSelectedIds,
        group: group,
      };

      dispatch(addFloorPlan(floorPlanData));

      // [SEÇİM MODU] Orijinal nesneleri SİLMEK yerine GIZLE.
      // Mod sonunda "Mimariyi Geri Yükle" ile geri görünür hale gelirler.
      // Mimari ilk haline döner, kullanıcı seçim sırasında tekrar
      // seçemediği için iki kez kullanılması da önlenmiş olur.
      selectedObjects.forEach(obj => {
        obj.visible = false;
        obj.userData = obj.userData || {};
        obj.userData._hiddenForSelection = true;
        hiddenObjectsRef.current.push(obj);
      });
      setHiddenCount(hiddenObjectsRef.current.length);
      setSelectionsSaved(false); // Yeni seçim → tekrar kaydetme gerek

      // ✅ Seçim modundan çık ve selectedFloorType'ı temizle
      setSelectedFloorType(null);
      dispatch(resetOperation());
    }, 100); // 100ms bekle
  };

  // ✅ Vaziyet Planı oluştur
  const createSitePlan = () => {
    if (!scene || !selectedObjectIds || selectedObjectIds.length === 0) {
      console.warn("Scene bulunamadı veya seçili nesne yok");
      return;
    }

    // selectedObjectIds'i kopyala (clearSelection sonrasında kaybolacak için)
    const currentSelectedIds = [...selectedObjectIds];
    // Set ile O(1) lookup — Array.includes O(n) ile 3700×3700 = 13M karşılaştırmadan kaçın
    const selectedIdSet = new Set(currentSelectedIds);

    // Seçimi iptal et - bu şekilde objeler orijinal renklerine döner
    dispatch(clearSelection());

    // Kısa bir süre bekleyerek nesnelerin orijinal renklerine dönmesini sağla
    setTimeout(() => {
      const selectedObjects = [];

      // Sadece üst seviye seçili nesneleri bul (nested children'ları dahil etme)
      scene.traverse((child) => {
        if (child.userData?.id && selectedIdSet.has(child.userData.id)) {
          // Bu nesnenin parent'ı da seçili mi kontrol et
          let parentAlsoSelected = false;
          let parent = child.parent;
          while (parent && parent.type !== 'Scene') {
            if (parent.userData?.id && selectedIdSet.has(parent.userData.id)) {
              parentAlsoSelected = true;
              break;
            }
            parent = parent.parent;
          }
          // Sadece parent'ı seçili olmayan nesneleri ekle
          if (!parentAlsoSelected) {
            selectedObjects.push(child);
          }
        }
      });

      if (selectedObjects.length === 0) {
        console.warn("Seçili nesneler scene'de bulunamadı");
        return;
      }

      const group = new THREE.Group();
      selectedObjects.forEach(obj => {
        // ✅ World matrix'leri CLONE'DAN ÖNCE topla (blok transform'ları için kritik)
        // collectWorldMatrices pattern hatch children'ı ATLAR (orderedMatrices ile senkron)
        const originalMatrices = collectWorldMatrices(obj);

        const clonedObj = obj.clone();

        // Pattern hatch LineSegments'lerini clone'dan kaldır.
        // Orijinal geometry korunur; hatch group'ları boş kalır (layout'ta görünmez).
        removePatternHatchChildren(clonedObj);

        // Geometry/material deep clone + renk geri yükleme + seçim temizliği TEK geçişte.
        clonedObj.traverse((clonedChild) => {
          if (clonedChild.geometry) clonedChild.geometry = clonedChild.geometry.clone();
          if (clonedChild.material) {
            clonedChild.material = Array.isArray(clonedChild.material)
              ? clonedChild.material.map(m => m.clone())
              : clonedChild.material.clone();
            const origColor = clonedChild.userData?.originalColor;
            if (origColor !== undefined) {
              if (Array.isArray(clonedChild.material)) {
                clonedChild.material.forEach((mat, i) => {
                  if (mat.color && Array.isArray(origColor) && origColor[i] !== undefined) mat.color.setHex(origColor[i]);
                });
              } else if (clonedChild.material.color && typeof origColor === 'number') {
                clonedChild.material.color.setHex(origColor);
              }
            }
          }
          if (clonedChild.userData) {
            delete clonedChild.userData.isSelected;
            delete clonedChild.userData.selectedTime;
          }
        });

        // ✅ World transform'u geometry'ye bake et (orijinal matrisler kullanarak)
        bakeWorldTransform(clonedObj, originalMatrices);

        group.add(clonedObj);
      });

      // Matrix'leri güncelle (bounding box doğru hesaplansın)
      group.updateMatrixWorld(true);

      // Sadece görünen geometry'leri hesaba katarak bounding box hesapla
      const boundingBox = calculateVisibleBoundingBox(group);
      const size = boundingBox.getSize(new THREE.Vector3());
      const center = boundingBox.getCenter(new THREE.Vector3());

      // Objeleri merkeze normalize et (geometry vertex'lerini kaydır)
      offsetAllGeometries(group, center);

      const sitePlanData = {
        name: "Vaziyet Planı",
        type: "site",
        width: Math.ceil(size.x) + 2 * DOCUMENT_OFFSET,
        height: Math.ceil(size.y) + 2 * DOCUMENT_OFFSET + headerHeight,
        objects: selectedObjects,
        objectsIds: currentSelectedIds,
        group: group,
      };

      dispatch(addSitePlan(sitePlanData));

      // [SEÇİM MODU] Silmek yerine GIZLE (createFloorPlan ile aynı mantık)
      selectedObjects.forEach(obj => {
        obj.visible = false;
        obj.userData = obj.userData || {};
        obj.userData._hiddenForSelection = true;
        hiddenObjectsRef.current.push(obj);
      });
      setHiddenCount(hiddenObjectsRef.current.length);
      setSelectionsSaved(false); // Yeni seçim → tekrar kaydetme gerek

      // ✅ Seçim modundan çık
      dispatch(resetOperation());
    }, 100); // 100ms bekle
  };

  // [SEÇİM MODU] Tüm gizlenen orijinal nesneleri scene'e geri görünür yap.
  // Mimari paftası ilk haline döner — KENDI ORİJİNAL RENKLERİ ile (selection
  // sırasında değişen kırmızı/yeşil rengin geri yüklenmesi). floorPlanSlice'taki
  // kayıtlar korunur. selectionsSaved=true → "Proje Oluştur" butonu görünür olur.
  const saveSelectionsAndRestore = () => {
    if (hiddenObjectsRef.current.length === 0) return;
    // Önce selection state'i tamamen temizle (selected obj'ler rengi düzelsin)
    dispatch(clearSelection());

    hiddenObjectsRef.current.forEach(obj => {
      obj.visible = true;
      // Selection meta'sını temizle (rengi geri yüklemek için)
      obj.traverse(child => {
        if (child.userData) {
          delete child.userData.isSelected;
          delete child.userData.selectedTime;
          // Selection rengi orijinal'e döndür (userData.originalColor varsa)
          const origColor = child.userData.originalColor;
          if (origColor !== undefined && child.material) {
            if (Array.isArray(child.material) && Array.isArray(origColor)) {
              child.material.forEach((mat, i) => {
                if (mat.color && origColor[i] !== undefined) mat.color.setHex(origColor[i]);
              });
            } else if (child.material.color && typeof origColor === 'number') {
              child.material.color.setHex(origColor);
            }
          }
        }
        if (child.userData?._hiddenForSelection) {
          delete child.userData._hiddenForSelection;
        }
      });
    });
    hiddenObjectsRef.current = [];
    setHiddenCount(0);
    setSelectionsSaved(true); // "Proje Oluştur" butonu görünür olur
  };

  // Redux commandType değiştiğinde activeButton'ı güncelle
  useEffect(() => { 

    if (commandType === COMMANDS.DRAW_ProjectSTRIP) {
      setActiveButton("ProjectStrip");
    } else if (commandType === COMMANDS.DRAW_ProjectSTAKE) {
      setActiveButton("ProjectStake");
    } else {
      setActiveButton(null);
    }
  }, [commandType]);

  const toolLabelMap = {
    ProjectStrip: "Topraklama Şeridi",
    ProjectStake: "Topraklama Çubuğu",
  };

  return (
    <div className={`toolProject ${isOpen ? "open" : "closed"}`}>
      {isOpen && (
        <div className="toolProject-left">

              {/* [SEÇİM MODU] Proje Oluştur sadece seçimler kaydedildikten
                  ve en az bir kat planı varsa görünür */}
              {selectionsSaved && floorPlansCount > 0 && (
                <button
                  title="Proje Oluştur"
                  className="toolProjectButton"
                  style={{ width: `${buttonSize}px`, height: `${buttonSize}px` }}
                  onClick={() => updateAllProject()}
                >
                  {getIcon("projectCreate", 22)}
                </button>
              )}

              <button
                title="Dokümanları Güncelle"
                className="toolProjectButton"
                style={{ width: `${buttonSize}px`, height: `${buttonSize}px` }}
                onClick={() => dispatch(incrementUpdateCounter())}
              >
                {getIcon("docUpdate", 22)}
              </button>

              <div className="floor-menu-container" ref={floorMenuTriggerRef} style={{ position: 'relative' }}>
                <button
                  title={isFloorPlanMode ? "Seçimi Onayla (Sağ Tıkla)" : "Kat Planı Ekle"}
                  className={`toolProjectButton ${isFloorPlanMode ? 'active' : ''} ${floorMenuOpen ? 'menu-open' : ''}`}
                  style={{
                    width: `${buttonSize}px`,
                    height: `${buttonSize}px`,
                    outline: isFloorPlanMode ? '2px solid #00ff00' : floorMenuOpen ? '2px solid #4a90d9' : 'none'
                  }}
                  onClick={handleFloorPlanButtonClick}
                >
                  {getIcon("floorPlan", buttonSize, buttonSize)}
                </button>

                {/* Açılır Menü */}
                {floorMenuOpen && (
                  <div className="floor-dropdown-menu" style={{ top: floorDropdownPos.top, left: floorDropdownPos.left, minWidth: floorDropdownPos.width }}>
                    {getFloorMenuOptions().map((option, index) => (
                      <button
                        key={`${option.type}-${option.number}-${index}`}
                        className="floor-menu-item"
                        onClick={() => handleFloorTypeSelect(option)}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <button
                title="Kat Planı Ayarları"
                className="toolProjectButton"
                style={{
                  width: `${buttonSize}px`,
                  height: `${buttonSize}px`,
                  fontSize: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={() => setIsFloorSettingsOpen(true)}
              >
                &#9881;
              </button>
              <button
                title={isSitePlanMode ? "Seçimi Onayla (Sağ Tıkla)" : "Vaziyet Planı Ekle"}
                className={`toolProjectButton ${isSitePlanMode ? 'active' : ''}`}
                style={{
                  width: `${buttonSize}px`,
                  height: `${buttonSize}px`,
                  outline: isSitePlanMode ? '2px solid #00ff00' : 'none'
                }}
                onClick={startSitePlanSelection}
              >
                {getIcon("sitePlan", buttonSize, buttonSize)}
              </button>
              <button
                title={isDeleteMode ? "Enter: Sil | ESC: İptal" : "Sahneden Sil"}
                className={`toolProjectButton ${isDeleteMode ? 'active' : ''}`}
                style={{
                  width: `${buttonSize}px`,
                  height: `${buttonSize}px`,
                  outline: isDeleteMode ? '2px solid #ff4444' : 'none',
                  fontSize: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
                onClick={startDeleteSelection}
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke={isDeleteMode ? '#ff4444' : '#fff'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <polyline points="3 6 5 6 21 6" />
                  <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
                  <path d="M10 11v6" />
                  <path d="M14 11v6" />
                  <path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" />
                </svg>
              </button>
              {/* [SEÇİM MODU] Seçimleri kaydet: restore + Proje Oluştur'u aktif et */}
              <button
                title={hiddenCount > 0
                  ? `Seçimleri Kaydet (${hiddenCount} nesne gizlendi)`
                  : 'Seçim yapılmadı'}
                className="toolProjectButton"
                style={{
                  width: `${buttonSize}px`,
                  height: `${buttonSize}px`,
                  outline: hiddenCount > 0 ? '2px solid #00cc66' : 'none',
                  opacity: hiddenCount > 0 ? 1 : 0.4,
                  fontSize: '18px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  cursor: hiddenCount > 0 ? 'pointer' : 'not-allowed',
                }}
                onClick={saveSelectionsAndRestore}
                disabled={hiddenCount === 0}
              >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke={hiddenCount > 0 ? '#00cc66' : '#888'} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M19 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11l5 5v11a2 2 0 0 1-2 2z" />
                  <polyline points="17 21 17 13 7 13 7 21" />
                  <polyline points="7 3 7 8 15 8" />
                </svg>
              </button>
        </div>
      )}

      <div className="toolProject-toggle">
        <button
          className="toolButton toggleArrowButton"
          onClick={() => setIsOpen((prev) => !prev)}
        >
          {isOpen ? "◀" : "Proje▶"}
        </button>
      </div>

      <FloorPlanSettingsPopup
        isOpen={isFloorSettingsOpen}
        onClose={() => setIsFloorSettingsOpen(false)}
      />
    </div>
  );
};

export default ToolProject;